import { motion } from 'framer-motion';
import {
  FaBug,
  FaLightbulb,
  FaHandshake,
  FaBuilding,
  FaStar,
  FaBookOpen,
  FaNewspaper,
  FaPlug,
  FaMicrophone,
  FaCheck,
  FaPaperPlane,
  FaPen,
  FaTimes,
  FaSpinner,
  FaExclamationCircle,
} from 'react-icons/fa';
import type { IntakeType, SupportProposal } from '../../stores/support-chat.store';

/**
 * The structured card the AI's "I think we have enough to file" turn
 * renders into. User clicks Submit (creates the row), Edit (re-opens
 * the conversation for refinement), or Cancel (drops it). NO ticket
 * is filed until the user clicks Submit.
 *
 * Visual states:
 *   - pending     interactive [Submit] [Edit] [Cancel]
 *   - submitting  spinner on Submit, all buttons disabled
 *   - submitted   green check + "Filed" stamp, buttons removed
 *   - cancelled   dimmed, "Cancelled" stamp, buttons removed
 *   - editing     "Asking AI what to change…" stamp; AI follow-up
 *                 message will arrive next
 *   - error       red banner with retry-able [Submit] [Cancel]
 */

const TYPE_META: Record<
  IntakeType,
  { label: string; icon: React.ComponentType<{ className?: string }>; accent: string }
> = {
  bug_report: { label: 'Bug report', icon: FaBug, accent: 'text-(--ee-tw-red-500)' },
  feature_request: {
    label: 'Feature request',
    icon: FaLightbulb,
    accent: 'text-(--ee-tw-yellow-500)',
  },
  partnership_inquiry: {
    label: 'Partnership inquiry',
    icon: FaHandshake,
    accent: 'text-(--ee-tw-cyan-500)',
  },
  enterprise_licensing: {
    label: 'Enterprise licensing',
    icon: FaBuilding,
    accent: 'text-(--ee-tw-indigo-500)',
  },
  testimonial: { label: 'Testimonial', icon: FaStar, accent: 'text-(--ee-tw-amber-500)' },
  content_contribution: {
    label: 'Your story',
    icon: FaBookOpen,
    accent: 'text-(--ee-tw-emerald-500)',
  },
  press_inquiry: { label: 'Press / media', icon: FaNewspaper, accent: 'text-(--ee-tw-sky-500)' },
  integration_request: {
    label: 'Integration request',
    icon: FaPlug,
    accent: 'text-(--ee-tw-orange-500)',
  },
  speaker_request: {
    label: 'Speaker request',
    icon: FaMicrophone,
    accent: 'text-(--ee-tw-pink-500)',
  },
};

interface ProposalCardProps {
  proposal: SupportProposal;
  onSubmit: () => void;
  onEdit: () => void;
  onCancel: () => void;
}

// Fallback when the AI emits an unknown intake type (or the type schema
// drifts ahead of this map). Avoids a TypeError on `meta.icon` and just
// renders the proposal with a neutral label.
const UNKNOWN_TYPE_META = {
  label: 'Support request',
  icon: FaLightbulb,
  accent: 'text-(--ee-tw-gray-500)',
} as const;

export default function ProposalCard({ proposal, onSubmit, onEdit, onCancel }: ProposalCardProps) {
  const meta = TYPE_META[proposal.type] ?? UNKNOWN_TYPE_META;
  const Icon = meta.icon;
  const status = proposal.status;
  const isFinal = status === 'submitted' || status === 'cancelled' || status === 'editing';

  return (
    <motion.div
      role="region"
      aria-label={`Proposed ${meta.label.toLowerCase()}`}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className={`mt-2 mb-1 rounded-2xl border bg-(--ee-surface) shadow-sm overflow-hidden ${
        status === 'cancelled'
          ? 'border-(--ee-tw-gray-200-line) opacity-60'
          : status === 'submitted'
            ? 'border-(--ee-tw-green-200-line)'
            : status === 'error'
              ? 'border-(--ee-tw-red-200-line)'
              : 'border-(--ee-tw-gray-200-line)'
      }`}
    >
      {/* Header: type pill + label. Status badge sits in an
          aria-live region so screen readers announce transitions
          (submitted, cancelled, editing). */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-(--ee-tw-gray-100-line)">
        <Icon className={`text-sm ${meta.accent}`} />
        <span className="text-xs font-semibold uppercase tracking-wider text-(--ee-tw-gray-500)">
          {meta.label}
        </span>
        <span role="status" aria-live="polite" className="ml-auto inline-flex">
          {status === 'submitted' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-(--ee-tw-green-100-bg) text-(--ee-tw-green-700)">
              <FaCheck className="text-[10px]" aria-hidden="true" /> Filed
            </span>
          )}
          {status === 'cancelled' && (
            <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-(--ee-tw-gray-100-bg) text-(--ee-tw-gray-500)">
              Cancelled
            </span>
          )}
          {status === 'editing' && (
            <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-(--ee-tw-blue-50-bg) text-(--ee-tw-blue-600)">
              Editing
            </span>
          )}
        </span>
      </div>

      {/* Body: title + description. Description capped at ~12rem with
          internal scroll so a long content_contribution (up to 10k
          chars) doesn't push Submit/Edit/Cancel offscreen. The full
          string is still POSTed on submit; this only affects the
          in-chat preview. */}
      <div className="px-4 py-3 space-y-2">
        <p className="text-sm font-semibold text-(--ee-tw-gray-900) leading-snug">
          {proposal.title}
        </p>
        <p className="text-sm text-(--ee-tw-gray-600) leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto">
          {proposal.description}
        </p>
        {proposal.consent_given ? (
          <p className="inline-flex items-center gap-1 text-xs text-(--ee-tw-gray-500)">
            <FaCheck className="text-(--ee-tw-green-600) text-[10px]" aria-hidden="true" />
            Consent recorded
          </p>
        ) : null}
      </div>

      {/* Error banner */}
      {status === 'error' && proposal.errorMessage ? (
        <div className="mx-4 mb-3 px-3 py-2 rounded-lg bg-(--ee-tw-red-50-bg) border border-(--ee-tw-red-100-line) text-xs text-(--ee-tw-red-700) flex items-start gap-2">
          <FaExclamationCircle className="mt-0.5 flex-shrink-0" />
          <span>{proposal.errorMessage}</span>
        </div>
      ) : null}

      {/* Actions: hidden in final states; shown for pending/submitting/error */}
      {!isFinal ? (
        <div className="flex items-center gap-2 px-4 pb-3 pt-1">
          <button
            type="button"
            onClick={onSubmit}
            disabled={status === 'submitting'}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-xl bg-primary text-(--ee-on-accent) hover:bg-primary/90 active:scale-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? (
              <>
                <FaSpinner className="text-[12px] animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                <FaPaperPlane className="text-[12px]" />
                Submit
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onEdit}
            disabled={status === 'submitting'}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl border border-(--ee-tw-gray-200-line) text-(--ee-tw-gray-700) hover:bg-(--ee-tw-gray-50-bg) active:scale-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FaPen className="text-[11px]" />
            Edit
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={status === 'submitting'}
            aria-label="Cancel submission"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl text-(--ee-tw-gray-500) hover:bg-(--ee-tw-gray-50-bg) active:scale-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FaTimes className="text-[11px]" />
          </button>
        </div>
      ) : null}
    </motion.div>
  );
}
